import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import HistoryIcon from '@mui/icons-material/History';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import StorageIcon from '@mui/icons-material/Storage';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';
import { formatDateInput, formatDateTimeDisplay, todayDateInput } from '../../utils/dateUtils';

const COPY = {
  ar: {
    title: 'تقرير التدقيق',
    subtitle: 'استعراض موحد لعمليات التغيير والعرض والتحديث في كامل النظام.',
    search: 'بحث في الإجراء، السجل، المستخدم، أو القيمة',
    source: 'المصدر',
    allSources: 'كل المصادر',
    action: 'الإجراء',
    allActions: 'كل الإجراءات',
    user: 'المستخدم',
    allUsers: 'كل المستخدمين',
    from: 'من تاريخ',
    to: 'إلى تاريخ',
    refresh: 'تحديث',
    reset: 'إعادة ضبط',
    total: 'إجمالي العمليات',
    sources: 'مصادر السجلات',
    users: 'المستخدمون',
    lastChange: 'آخر تغيير',
    noData: 'لا توجد عمليات ضمن الفلاتر الحالية.',
    loadError: 'تعذر تحميل تقرير التدقيق.',
    table: {
      date: 'التاريخ',
      user: 'المستخدم',
      role: 'الدور',
      source: 'المصدر',
      action: 'الإجراء',
      table: 'الجدول/السجل',
      recordId: 'معرف السجل',
      field: 'الحقل',
      old: 'القيمة السابقة',
      new: 'القيمة الجديدة',
      ip: 'عنوان IP',
    },
    page: 'صفحة',
    next: 'التالي',
    previous: 'السابق',
    of: 'من',
    unknown: 'غير معروف',
  },
  en: {
    title: 'Audit Report',
    subtitle: 'Unified review of view, update, and change operations across the system.',
    search: 'Search action, record, user, or value',
    source: 'Source',
    allSources: 'All sources',
    action: 'Action',
    allActions: 'All actions',
    user: 'User',
    allUsers: 'All users',
    from: 'From',
    to: 'To',
    refresh: 'Refresh',
    reset: 'Reset',
    total: 'Total events',
    sources: 'Audit sources',
    users: 'Users',
    lastChange: 'Last change',
    noData: 'No audit events match the current filters.',
    loadError: 'Unable to load audit report.',
    table: {
      date: 'Date',
      user: 'User',
      role: 'Role',
      source: 'Source',
      action: 'Action',
      table: 'Table / record',
      recordId: 'Record ID',
      field: 'Field',
      old: 'Old value',
      new: 'New value',
      ip: 'IP address',
    },
    page: 'Page',
    next: 'Next',
    previous: 'Previous',
    of: 'of',
    unknown: 'Unknown',
  },
  pt: {
    title: 'Relatório de auditoria',
    subtitle: 'Revisão unificada de visualizações, atualizações e alterações em todo o sistema.',
    search: 'Pesquisar usuário, fonte, ação, tabela, registro, campo ou valor',
    source: 'Fonte',
    allSources: 'Todas as fontes',
    action: 'Ação',
    allActions: 'Todas as ações',
    user: 'Usuário',
    allUsers: 'Todos os usuários',
    from: 'De',
    to: 'Até',
    refresh: 'Atualizar',
    reset: 'Redefinir',
    total: 'Eventos totais',
    sources: 'Fontes de auditoria',
    users: 'Usuários',
    lastChange: 'Última alteração',
    noData: 'Nenhum evento de auditoria corresponde aos filtros atuais.',
    loadError: 'Não foi possível carregar o relatório de auditoria.',
    table: {
      date: 'Data',
      user: 'Usuário',
      role: 'Função',
      source: 'Fonte',
      action: 'Ação',
      table: 'Tabela / registro',
      recordId: 'ID do registro',
      field: 'Campo',
      old: 'Valor antigo',
      new: 'Valor novo',
      ip: 'Endereço IP',
    },
    page: 'Página',
    next: 'Próximo',
    previous: 'Anterior',
    of: 'de',
    unknown: 'Desconhecido',
  },
};

const todayInput = () => todayDateInput();

const daysAgoInput = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return formatDateInput(date);
};

function compactValue(value) {
  if (value === null || value === undefined || value === '') return '-';
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  if (text.length <= 120) return text;
  return `${text.slice(0, 120)}...`;
}

function formatDate(value, language) {
  return formatDateTimeDisplay(value, value || '-');
}

const statCards = [
  { key: 'total', icon: <AssessmentIcon />, tone: 'gold' },
  { key: 'sources', icon: <StorageIcon />, tone: 'blue' },
  { key: 'users', icon: <PersonSearchIcon />, tone: 'green' },
  { key: 'lastChange', icon: <HistoryIcon />, tone: 'rose' },
];

const toneColor = {
  gold: { color: COLORS.gold, soft: COLORS.goldSoft },
  blue: { color: COLORS.blue, soft: COLORS.blueSoft },
  green: { color: COLORS.green, soft: COLORS.greenSoft },
  rose: { color: COLORS.rose, soft: COLORS.roseSoft },
};

export default function AuditReportPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : i18n.language === 'pt' ? 'pt' : 'en';
  const text = COPY[lang];
  const isRtl = lang === 'ar';
  const [filters, setFilters] = useState({
    search: '',
    source: 'all',
    action: 'all',
    user: 'all',
    from: daysAgoInput(30),
    to: todayInput(),
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [data, setData] = useState({ items: [], total: 0, sources: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const loadAudit = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(rowsPerPage),
        from: filters.from,
        to: filters.to,
      });
      if (filters.search.trim()) params.set('search', filters.search.trim());
      if (filters.source !== 'all') params.set('source', filters.source);
      if (filters.action !== 'all') params.set('action', filters.action);
      if (filters.user !== 'all') params.set('user', filters.user);

      const response = await api.get(`/audit/unified?${params.toString()}`);
      setData(response.data || { items: [], total: 0, sources: [], summary: {} });
    } catch (err) {
      setError(err.response?.data?.details || err.response?.data?.message || text.loadError);
    } finally {
      setLoading(false);
    }
  }, [filters.action, filters.from, filters.search, filters.source, filters.to, filters.user, page, rowsPerPage, text.loadError]);

  useEffect(() => {
    loadAudit();
  }, [loadAudit]);

  const stats = useMemo(() => {
    const summary = data.summary || {};
    return {
      total: Number(summary.total || data.total || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-US'),
      sources: Number((summary.sources || []).length || data.sources?.length || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-US'),
      users: Number((summary.users || []).filter((item) => item.user_id).length || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-US'),
      lastChange: summary.lastChangeAt ? formatDate(summary.lastChangeAt, lang) : '-',
    };
  }, [data, lang]);

  const resetFilters = () => {
    setFilters({ search: '', source: 'all', action: 'all', user: 'all', from: daysAgoInput(30), to: todayInput() });
    setPage(1);
  };

  return (
    <Box
      className="smart-page-container"
      sx={{
        minHeight: '100vh',
        bgcolor: COLORS.page,
        width: '100%',
        maxWidth: 'var(--app-page-max)',
        mx: 'auto',
        overflowX: 'hidden',
        direction: isRtl ? 'rtl' : 'ltr',
      }}
    >
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
              {text.title}
            </Typography>
            <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>
              {text.subtitle}
            </Typography>
          </Box>
          <Button
            onClick={loadAudit}
            variant="contained"
            startIcon={!isRtl ? <RefreshIcon /> : null}
            endIcon={isRtl ? <RefreshIcon /> : null}
            sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldDark } }}
          >
            {text.refresh}
          </Button>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
          gap: 2,
          mb: 3,
        }}
      >
        {statCards.map((card) => {
          const palette = toneColor[card.tone];
          return (
            <Paper key={card.key} elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 112 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: palette.soft, color: palette.color, display: 'grid', placeItems: 'center' }}>
                  {card.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: palette.color, fontWeight: 900, fontSize: 13 }}>
                    {text[card.key]}
                  </Typography>
                  <Typography sx={{ color: COLORS.text, fontWeight: 900, fontSize: card.key === 'lastChange' ? 16 : 28, mt: 0.4 }}>
                    {stats[card.key]}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Box>

      <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }}>
          <TextField
            value={filters.search}
            onChange={(event) => updateFilter('search', event.target.value)}
            placeholder={text.search}
            size="small"
            sx={{ flex: 1, minWidth: { lg: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label={text.source}
            value={filters.source}
            onChange={(event) => updateFilter('source', event.target.value)}
            size="small"
            sx={{ width: { xs: '100%', lg: 240 } }}
          >
            <MenuItem value="all">{text.allSources}</MenuItem>
            {(data.sources || []).map((source) => (
              <MenuItem key={source.key} value={source.key}>
                {source.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={text.action}
            value={filters.action}
            onChange={(event) => updateFilter('action', event.target.value)}
            size="small"
            sx={{ width: { xs: '100%', lg: 180 } }}
          >
            <MenuItem value="all">{text.allActions}</MenuItem>
            {(data.summary?.actions || []).map((action) => (
              <MenuItem key={action.action || 'unknown'} value={action.action || ''}>
                {action.action || text.unknown}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={text.user}
            value={filters.user}
            onChange={(event) => updateFilter('user', event.target.value)}
            size="small"
            sx={{ width: { xs: '100%', lg: 220 } }}
          >
            <MenuItem value="all">{text.allUsers}</MenuItem>
            {(data.summary?.users || []).map((user) => (
              <MenuItem key={user.username || user.user_id || 'unknown'} value={user.username || ''}>
                {user.username || text.unknown}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="date"
            label={text.from}
            value={filters.from}
            onChange={(event) => updateFilter('from', event.target.value)}
            size="small"
            sx={{ width: { xs: '100%', lg: 180 } }}
            InputLabelProps={{ shrink: true }}
            InputProps={{ startAdornment: <CalendarMonthIcon sx={{ color: COLORS.muted, mx: 0.5 }} /> }}
          />
          <TextField
            type="date"
            label={text.to}
            value={filters.to}
            onChange={(event) => updateFilter('to', event.target.value)}
            size="small"
            sx={{ width: { xs: '100%', lg: 180 } }}
            InputLabelProps={{ shrink: true }}
          />
          <Button onClick={resetFilters} variant="outlined" startIcon={!isRtl ? <FilterAltIcon /> : null} endIcon={isRtl ? <FilterAltIcon /> : null}>
            {text.reset}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontWeight: 900, color: COLORS.text }}>
            {text.title}
          </Typography>
          <Chip label={`${Number(data.total || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-US')} ${text.total}`} sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 900 }} />
        </Stack>
        <Divider />

        <TableContainer sx={{ maxHeight: 'calc(100vh - 430px)' }}>
          <Table stickyHeader size="small" sx={{ minWidth: 1480 }}>
            <TableHead>
              <TableRow>
                {Object.values(text.table).map((label) => (
                  <TableCell
                    key={label}
                    sx={{
                      bgcolor: '#f4efe4',
                      color: COLORS.text,
                      fontWeight: 900,
                      borderBottom: `2px solid ${COLORS.gold}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : data.items?.length ? (
                data.items.map((row, index) => (
                  <TableRow key={`${row.source_key}-${row.audit_id || index}-${row.event_at}`} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: COLORS.muted }}><span className="date-ltr">{formatDate(row.event_at, lang)}</span></TableCell>
                    <TableCell>{row.username || (row.user_id ? `${text.unknown} #${row.user_id}` : '-')}</TableCell>
                    <TableCell>{row.role_name || '-'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.source_label || row.source_key} sx={{ bgcolor: COLORS.blueSoft, color: COLORS.text, fontWeight: 800 }} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>{row.action || '-'}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800, fontSize: 13 }}>{row.table_name || row.entity_type || '-'}</Typography>
                      <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>{row.request_method || '-'}</Typography>
                    </TableCell>
                    <TableCell>{row.record_id || row.entity_id || '-'}</TableCell>
                    <TableCell>{row.field_name || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Tooltip title={row.old_value || ''} placement="top">
                        <Typography sx={{ fontSize: 12, color: COLORS.muted, wordBreak: 'break-word' }}>
                          {compactValue(row.old_value)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Tooltip title={row.new_value || ''} placement="top">
                        <Typography sx={{ fontSize: 12, color: COLORS.text, wordBreak: 'break-word' }}>
                          {compactValue(row.new_value)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.ip_address || '-'}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 8, color: COLORS.muted }}>
                    {text.noData}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />
        <TablePagination
          component="div"
          count={Number(data.total || 0)}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, nextPage) => setPage(nextPage + 1)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(1);
          }}
        />
      </Paper>
    </Box>
  );
}
